import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { FormFieldType, IAccount, ICustomSurvey, IFormField } from '../../../../../extViewmodels';
import { computeVisibleFields } from '../../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { clearHiddenFieldValues } from '../../../../../surveys/containers/SurveyResponses/shared/hooks/clearHiddenFieldValues';
import { SurveySections } from '../../../../../surveys/containers/SurveyResponses/shared/components/SurveySections';
import type { SectionedSurveyForm } from '../../../../../surveys/containers/SurveyResponses/shared/types';
import { Button } from '../../../../components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { THEME } from '../../../../components-v2/Theme/theme';
import { CompleteCheckIcon } from '../../../../components/svgs/icons/CompleteCheckIcon';
import { TextInput } from '../../../../components/TextInput';
import { CountrySelector } from '../../../../components/ValueSelectors/CountrySelector';
import { brandSecondary, darkGrayFontColor, mention, spanishBlue } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { flattenSurveyPartsToFields } from '../../shared/utils/flattenSurveyPartsToFields';
import { CustomSurveyFormData } from '../utils';

const { colors, spacing, typography, common } = THEME;

const styleSheet = StyleSheet.create({
	container: {
		maxWidth: 648,
		margin: '0 auto',
		width: '100%',
	},
	loadingContainer: {
		textAlign: 'center',
		padding: 32,
	},
	header: {
		backgroundColor: colors.background.light,
		paddingTop: 20,
		paddingBottom: 20,
		textAlign: 'center',
		minHeight: 110,
		alignItems: 'center',
		justifyContent: 'center',
		display: 'flex',
		flexDirection: 'column',
		marginBottom: 16,
	},
	logo: {
		width: '100%',
		maxWidth: 200,
		marginBottom: 12,
	},
	surveyTitle: {
		fontSize: 16,
		margin: 0,
		marginBottom: 8,
		color: colors.text.main,
	},
	companyName: {
		fontSize: 16,
		color: colors.text.light,
		margin: 0,
	},
	introText: {
		margin: '12px auto',
		marginBottom: 24,
		whiteSpace: 'pre-wrap',
		fontSize: 14,
		textAlign: 'left',
		paddingLeft: 24,
		paddingRight: 24,
	},
	formContent: {
		padding: '0 24px 24px 24px',
	},
	fieldContainer: {
		marginBottom: 16,
	},
	label: {
		display: 'block',
		marginBottom: 8,
		fontSize: 14,
		fontWeight: 600,
		color: darkGrayFontColor,
	},
	fieldRow: {
		display: 'flex',
		gap: 16,
	},
	fieldRowItem: {
		flex: 1,
	},
	fieldRowMargin: {
		marginBottom: 16,
	},
	addressSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		marginBottom: 16,
	},
	formControl: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	countrySelector: {
		width: '100%',
		pointerEvents: 'none',
		opacity: 0.6,
	},
	selectTrigger: {
		width: '100%',
	},
	checkboxRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		marginBottom: spacing.sm,
	},
	checkboxLabel: {
		fontSize: 14,
		color: colors.text.light,
		cursor: 'pointer',
	},
	fileUploadPlaceholder: {
		boxSizing: 'border-box',
		width: 350,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 12,
		textAlign: 'center',
		padding: spacing.md,
		background: mention,
		border: `2px dashed ${colors.primary.main}`,
		borderRadius: common.borderRadius,
		boxShadow: '0px 0px 6px rgba(139,139,139, 0.1)',
		marginBottom: spacing.md,
		opacity: 0.6,
		cursor: 'not-allowed',
	},
	fileUploadText: {
		color: spanishBlue,
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.semibold,
		marginTop: spacing.sm,
	},
	fileUploadFooter: {
		color: brandSecondary,
		fontSize: typography.fontSizes.xs,
		marginTop: spacing.xs,
	},
	submitContainer: {
		marginTop: 24,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	confirmationContainer: {
		textAlign: 'center',
		padding: 32,
	},
	confirmationIcon: {
		marginBottom: 24,
		display: 'flex',
		justifyContent: 'center',
	},
	confirmationHeader: {
		fontSize: 24,
		fontWeight: 600,
		marginBottom: 16,
		color: colors.text.main,
	},
	confirmationContent: {
		fontSize: 14,
		color: colors.text.light,
		marginBottom: 24,
	},
	backButton: {
		marginTop: 24,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
});

interface PreviewCustomSurveyFormProps {
	survey: ICustomSurvey;
	account?: IAccount;
}

export const PreviewCustomSurveyForm: React.FC<PreviewCustomSurveyFormProps> = ({ survey: baseSurvey, account }) => {
	const [isSubmitted, setIsSubmitted] = React.useState(false);
	const [previewSelections, setPreviewSelections] = React.useState<Record<string, string>>({});
	const { watch } = useFormContext<CustomSurveyFormData>();
	const formValues = watch();

	// Get intro from form values (rich text editor state), fallback to base survey (API data)
	const introSource = formValues.options?.intro ?? baseSurvey?.options?.intro;

	// Extract and render intro message from rich content source
	const introMessage = React.useMemo(() => {
		try {
			let content = null;
			// If it's a rich text editor state object with the method
			if (introSource && typeof introSource === 'object' && 'getRawRichTextContent' in introSource) {
				const richContent = introSource.getRawRichTextContent();
				// getRawRichTextContent() returns an object with a document property
				if (richContent && typeof richContent === 'object' && 'document' in richContent) {
					content = richContent.document;
				}
			}
			// If it's API data with a document property (for saved surveys)
			else if (introSource && typeof introSource === 'object' && 'document' in introSource) {
				content = (introSource as any).document;
			}

			if (!content || typeof content !== 'string') return null;

			// Clean up HTML: remove lev-content tags and p-tag styles
			const cleaned = content
				.replace(/<lev-content[^>]*>/g, '')
				.replace(/<\/lev-content>/g, '')
				.replace(/<p[^>]*>/g, '<p>')
				.replace(/<\/p>/g, '</p>');
			return <div className={css(styleSheet.introText)} dangerouslySetInnerHTML={{ __html: cleaned }} />;
		} catch (error) {
			console.error('Error getting intro text:', error);
			return null;
		}
	}, [introSource]);

	// Merge saved survey data with live form values for real-time preview
	const options = {
		...baseSurvey?.options,
		...(formValues.options as any),
	};

	const surveyForm = formValues.surveyForm ?? baseSurvey?.surveyForm;

	// Preview mirrors the editor's LIVE form state — unsaved edits included — matching the
	// V1 custom-survey editor and the other survey types.
	// - V2 (sectioned): the editor writes to `surveyForm.surveyParts.*`, so flatten the live
	//   parts into renderable fields (same flatten the save payload uses). The form's
	//   `customForm.fields` copy only refreshes on save and must NOT be the render source.
	// - V1 (flat): the editor edits `customForm.fields` directly, so that copy IS live.
	const liveV2Fields = flattenSurveyPartsToFields(surveyForm?.surveyParts);
	const allCustomFormFields = (
		liveV2Fields ??
		formValues.customForm?.fields ??
		baseSurvey?.customForm?.fields ??
		[]
	).filter((field: IFormField<string>) => !field.isHidden);

	// Compute which fields are visible based on surveyForm conditional visibility rules.
	// Fields with visibleWhen conditions are hidden when their conditions aren't met.
	const visibleFieldIds = React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) return undefined;
		return computeVisibleFields(surveyForm, previewSelections);
	}, [surveyForm, previewSelections]);

	// Get confirmation message from form values
	const confirmationSource = formValues.options?.confirmationMessage ?? baseSurvey?.options?.confirmationMessage;

	// Extract and render confirmation message from rich content source
	const confirmationMessage = React.useMemo(() => {
		try {
			let content = null;
			// If it's a rich text editor state object with the method
			if (
				confirmationSource &&
				typeof confirmationSource === 'object' &&
				'getRawRichTextContent' in confirmationSource
			) {
				const richContent = confirmationSource.getRawRichTextContent();
				// getRawRichTextContent() returns an object with a document property
				if (richContent && typeof richContent === 'object' && 'document' in richContent) {
					content = richContent.document;
				}
			}
			// If it's API data with a document property (for saved surveys)
			else if (confirmationSource && typeof confirmationSource === 'object' && 'document' in confirmationSource) {
				content = (confirmationSource as any).document;
			}

			if (!content || typeof content !== 'string') return null;

			// Clean up HTML: remove lev-content tags and p-tag styles
			const cleaned = content
				.replace(/<lev-content[^>]*>/g, '')
				.replace(/<\/lev-content>/g, '')
				.replace(/<p[^>]*>/g, '<p>')
				.replace(/<\/p>/g, '</p>');
			return <div dangerouslySetInnerHTML={{ __html: cleaned }} />;
		} catch (error) {
			console.error('Error getting confirmation text:', error);
			return null;
		}
	}, [confirmationSource]);

	if (!baseSurvey) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<p>Loading preview...</p>
			</div>
		);
	}

	const companyName = options.companyName || '';

	const handleSubmit = () => {
		setIsSubmitted(true);
		toast.success('Survey submitted (Preview only)');
	};

	const handleDropdownChange = (fieldId: string, value: string) => {
		setPreviewSelections(prev => {
			const next = { ...prev, [fieldId]: value };
			// Clear any answers whose questions became hidden after this change.
			// Uses the same surveyForm source the visibility computation uses.
			// Cascades to fixpoint so chained visibleWhen hides are also resolved.
			return clearHiddenFieldValues(surveyForm, next);
		});
	};

	const handleCheckboxChange = (fieldId: string, optionId: string, checked: boolean) => {
		setPreviewSelections(prev => {
			const current = prev[fieldId] ?? '';
			const selected = current.split(',').filter(Boolean);
			const updated = checked ? [...selected, optionId] : selected.filter(id => id !== optionId);
			const next = { ...prev, [fieldId]: updated.join(',') };
			// Clear any answers whose questions became hidden after this change.
			return clearHiddenFieldValues(surveyForm, next);
		});
	};

	const renderCustomField = (field: any) => {
		switch (field.fieldType) {
			case FormFieldType.String:
				return (
					<div key={field.id} className={css(styleSheet.fieldContainer)}>
						<label htmlFor={field.id} className={css(styleSheet.label)}>
							{field.label}
							{!field.isOptional && <span className={css(baseStyleSheet.required)}> *</span>}
						</label>
						<TextInput type='text' inputId={field.id} disabled placeholder={field.label} />
					</div>
				);
			case FormFieldType.Option: {
				const dropdownOptions: { id: string; label: string }[] =
					field.options?.map((opt: any) => ({
						id: (typeof opt === 'string' ? opt : opt.id) as string,
						label: (typeof opt === 'string' ? opt : opt.label) as string,
					})) ?? [];
				return (
					<div key={field.id} className={css(styleSheet.fieldContainer)}>
						<label htmlFor={field.id} className={css(styleSheet.label)}>
							{field.label}
							{!field.isOptional && <span className={css(baseStyleSheet.required)}> *</span>}
						</label>
						<Select
							// items lets SelectValue render the option label; without it base-ui shows the raw value (the id)
							items={dropdownOptions.map(opt => ({ value: opt.id, label: opt.label }))}
							value={previewSelections[field.id] || undefined}
							onValueChange={(val: string) => handleDropdownChange(field.id, val)}
						>
							<SelectTrigger id={field.id} styles={[styleSheet.selectTrigger]}>
								<SelectValue placeholder='Select option' />
							</SelectTrigger>
							<SelectContent>
								{dropdownOptions.map(opt => (
									<SelectItem key={opt.id} value={opt.id}>
										{opt.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				);
			}
			case FormFieldType.MultipleOptions:
				return (
					<div key={field.id} className={css(styleSheet.fieldContainer)}>
						<label className={css(styleSheet.label)}>
							{field.label}
							{!field.isOptional && <span className={css(baseStyleSheet.required)}> *</span>}
						</label>
						{field.options?.map((opt: any) => {
							const optLabel = typeof opt === 'string' ? opt : opt.label;
							const optId = typeof opt === 'string' ? opt : opt.id;
							const selectedValues = (previewSelections[field.id] ?? '').split(',').filter(Boolean);
							return (
								<div key={`${field.id}-${optId}`} className={css(styleSheet.checkboxRow)}>
									<Checkbox
										id={`${field.id}-${optId}`}
										checked={selectedValues.includes(optId)}
										onCheckedChange={(checked: boolean | 'indeterminate') =>
											handleCheckboxChange(field.id, optId, checked === true)
										}
									/>
									<label htmlFor={`${field.id}-${optId}`} className={css(styleSheet.checkboxLabel)}>
										{optLabel}
									</label>
								</div>
							);
						})}
					</div>
				);
			case FormFieldType.FileUpload:
				return (
					<div key={field.id} className={css(styleSheet.fieldContainer)}>
						<label htmlFor={field.id} className={css(styleSheet.label)}>
							{field.label}
							{!field.isOptional && <span className={css(baseStyleSheet.required)}> *</span>}
						</label>
						<div id={field.id} className={css(styleSheet.fileUploadPlaceholder)} aria-disabled='true'>
							<div className={css(styleSheet.fileUploadText)}>Drag and drop files to upload</div>
							<div className={css(styleSheet.fileUploadFooter)}>Click to browse device</div>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	// Show confirmation view after submission
	if (isSubmitted) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.confirmationContainer)}>
					<div className={css(styleSheet.confirmationIcon)}>
						<CompleteCheckIcon />
					</div>
					<h2 className={css(styleSheet.confirmationHeader)}>Your response has been recorded!</h2>
					{confirmationMessage ? (
						<div className={css(styleSheet.confirmationContent)}>{confirmationMessage}</div>
					) : (
						<p className={css(styleSheet.confirmationContent)}>Your response has been recorded.</p>
					)}
					<p className={css(styleSheet.confirmationContent)}>
						<span>In the live survey, your submitted responses will be displayed here.</span>
					</p>
					<div className={css(styleSheet.backButton)}>
						<Button type='button' variant='secondary' onClick={() => setIsSubmitted(false)}>
							Back to Form
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Get survey name from form values, fallback to base survey
	const surveyName = formValues.name ?? baseSurvey.name ?? 'Survey';

	// Determine which logo to display: custom uploaded image or account logo
	const logoUrl = options.image?.url || account?.preferences?.logo?.url;
	const shouldShowLogo = logoUrl && options.includeCompanyLogo !== false;

	return (
		<div className={css(styleSheet.container)}>
			{/* Survey Header with Logo, Title, and Company Name */}
			<section className={css(styleSheet.header)}>
				{shouldShowLogo && <img src={logoUrl} className={css(styleSheet.logo)} alt='Company Logo' />}
				<h2 className={css(styleSheet.surveyTitle)}>{surveyName}</h2>
				{companyName && <p className={css(styleSheet.companyName)}>{companyName}</p>}
			</section>

			{/* Intro message */}
			{introMessage ?? null}

			{/* Form Content */}
			<div className={css(styleSheet.formContent)}>
				{/* Contact Name Fields */}
				{options.contactNameOptions?.collect && (
					<>
						<div className={css(styleSheet.fieldContainer)}>
							<label htmlFor='firstName' className={css(styleSheet.label)}>
								First Name
								{options.contactNameOptions.require && <span className={css(baseStyleSheet.required)}> *</span>}
							</label>
							<TextInput type='text' inputId='firstName' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(styleSheet.fieldContainer)}>
							<label htmlFor='lastName' className={css(styleSheet.label)}>
								Last Name
								{options.contactNameOptions.require && <span className={css(baseStyleSheet.required)}> *</span>}
							</label>
							<TextInput type='text' inputId='lastName' disabled placeholder='PREVIEW ONLY' />
						</div>
					</>
				)}

				{/* Contact Email */}
				{options.contactEmailOptions?.collect && (
					<div className={css(styleSheet.fieldContainer)}>
						<label htmlFor='email' className={css(styleSheet.label)}>
							Email
							{options.contactEmailOptions.require && <span className={css(baseStyleSheet.required)}> *</span>}
						</label>
						<TextInput type='email' inputId='email' disabled placeholder='PREVIEW ONLY' />
					</div>
				)}

				{/* Contact Phone */}
				{options.contactPhoneNumberOptions?.collect && (
					<div className={css(styleSheet.fieldContainer)}>
						<label htmlFor='phoneNumber' className={css(styleSheet.label)}>
							Phone Number{' '}
							{!options.contactPhoneNumberOptions.require ? (
								<small style={{ fontSize: 11 }}>(Optional)</small>
							) : (
								<span className={css(baseStyleSheet.required)}>*</span>
							)}
						</label>
						<TextInput type='tel' inputId='phoneNumber' disabled placeholder='PREVIEW ONLY' />
					</div>
				)}

				{/* Contact Birthday */}
				{options.contactBirthdayOptions?.collect && (
					<div className={css(styleSheet.fieldContainer)}>
						<label htmlFor='birthday' className={css(styleSheet.label)}>
							Birthday{' '}
							{options.contactBirthdayOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : (
								<small style={{ fontSize: 11 }}>(Optional)</small>
							)}
						</label>
						<TextInput type='text' inputId='birthday' disabled placeholder='PREVIEW ONLY' />
					</div>
				)}

				{/* Contact Address */}
				{options.contactAddressOptions?.collect && (
					<div className={css(styleSheet.addressSection)}>
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='address1' className={css(styleSheet.label)}>
								Address Line 1{' '}
								{options.contactAddressOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : (
									<small style={{ fontSize: 11 }}>(Optional)</small>
								)}
							</label>
							<TextInput type='text' inputId='address1' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='address2' className={css(styleSheet.label)}>
								Address Line 2 <small style={{ fontSize: 11 }}>(Optional)</small>
							</label>
							<TextInput type='text' inputId='address2' disabled placeholder='PREVIEW ONLY' />
						</div>
						<div className={css(styleSheet.formControl)}>
							<div className={css(styleSheet.fieldRow)}>
								<div className={css(styleSheet.fieldRowItem)}>
									<label htmlFor='city' className={css(styleSheet.label)}>
										City{' '}
										{options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<TextInput type='text' inputId='city' disabled placeholder='PREVIEW ONLY' />
								</div>
								<div className={css(styleSheet.fieldRowItem)}>
									<label htmlFor='stateProvince' className={css(styleSheet.label)}>
										State/Province{' '}
										{options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<TextInput type='text' inputId='stateProvince' disabled placeholder='PREVIEW ONLY' />
								</div>
								<div className={css(styleSheet.fieldRowItem)}>
									<label htmlFor='postalCode' className={css(styleSheet.label)}>
										Postal Code{' '}
										{options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<TextInput type='text' inputId='postalCode' disabled placeholder='PREVIEW ONLY' />
								</div>
							</div>
						</div>
						<div className={css(styleSheet.formControl)}>
							<label id='country-label' className={css(styleSheet.label)}>
								Country{' '}
								{options.contactAddressOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : (
									<small style={{ fontSize: 11 }}>(Optional)</small>
								)}
							</label>
							<div
								role='combobox'
								aria-labelledby='country-label'
								aria-controls='country-options'
								aria-expanded='false'
							>
								<CountrySelector
									onSelectionChanged={() => {
										// Preview only - no action needed
									}}
									className={css(styleSheet.countrySelector)}
								/>
							</div>
						</div>
					</div>
				)}

				{/* Custom Form Fields */}
				<SurveySections
					surveyForm={surveyForm as SectionedSurveyForm | undefined}
					fields={allCustomFormFields}
					visibleFieldIds={visibleFieldIds}
					renderField={renderCustomField}
				/>

				{/* Submit Button */}
				<div className={css(styleSheet.submitContainer)}>
					<Button type='button' variant='primary' onClick={handleSubmit}>
						Submit
					</Button>
				</div>
			</div>
		</div>
	);
};
